import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Printer, ShieldCheck } from '@/components/icons';
import { api } from '@/lib/api';
import type { Run } from '@/lib/types';
import { formatDateTime, projectNameOf, runShortId, runTitle } from '@/lib/format';
import { Logo } from '@/components/layout/Logo';
import { ReportResults, ReportSummary } from '@/components/report/ReportView';
import { PerfReport } from '@/components/perf/PerfReport';
import { SecurityChecks, SecurityReport } from '@/components/report/SecurityReport';
import { Button, EmptyState, PageLoader, StatusPill } from '@/components/ui';

/** Public, read-only, print-friendly report. No authentication. */
export function SharedReportPage() {
  const { token = '' } = useParams();
  const run = useQuery({ queryKey: ['shared', token], queryFn: () => api.sharedReport(token), retry: false });

  return (
    <div className="min-h-dvh">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur dark:border-slate-800 dark:bg-slate-950/80">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-6">
          <Logo />
          <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
            <ShieldCheck className="size-4 text-emerald-500" /> Read-only shared report
          </span>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-6 px-6 py-10">
        {run.isPending ? (
          <PageLoader label="Loading report…" />
        ) : run.isError ? (
          <EmptyState title="This report link is not available" description="It may have expired or been revoked by its owner." />
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="text-xs font-semibold tracking-wider text-brand-600 uppercase">{projectNameOf(run.data)}</div>
                <h1 className="mt-1 flex flex-wrap items-center gap-3 text-3xl font-bold tracking-tight">
                  {runTitle(run.data)} <StatusPill status={run.data.status} />
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  <span className="font-mono">Run {runShortId(run.data)}</span> · {formatDateTime(run.data.finishedAt ?? run.data.queuedAt)}
                </p>
              </div>
              {!run.data.perfPlan && (
                <Button variant="secondary" className="no-print" onClick={() => window.print()} icon={<Printer className="size-4" />}>
                  Print / save PDF
                </Button>
              )}
            </div>
            {run.data.perfPlan ? (
              // Performance runs: the same report as in 1QA (it has its own Export as PDF).
              <PerfReport run={run.data} result={run.data.results?.[0]} title={runTitle(run.data)} />
            ) : run.data.securityPlan ? (
              // Security scans: the same grade, findings and checks as in 1QA.
              <SharedSecurity run={run.data} />
            ) : (
              <>
                <ReportSummary run={run.data} />
                <ReportResults run={run.data} publicToken={token} />
              </>
            )}
          </>
        )}
        <p className="pt-6 text-center text-xs text-slate-400">Generated by 1QA - test automation for everyone.</p>
      </main>
    </div>
  );
}

function SharedSecurity({ run }: { run: Run }) {
  const result = run.results?.[0];
  const error = result?.error ?? run.error;
  if (run.status === 'PASSED' || run.status === 'FAILED') {
    return (
      <>
        <SecurityReport findings={result?.findings ?? []} plan={run.securityPlan} />
        <SecurityChecks steps={result?.steps ?? []} />
        {result?.error && <p className="rounded-field bg-danger-subtle p-3 text-sm text-danger-text">{result.error}</p>}
      </>
    );
  }
  // Not finished with a grade (scanner error, cancelled, still running): say so instead of an empty "Grade A".
  return (
    <>
      <div className="rounded-card bg-danger-subtle p-4 ring-1 ring-border ring-inset">
        <p className="text-sm font-semibold text-danger-text">
          {run.status === 'CANCELLED'
            ? 'This scan was cancelled before it finished — no security grade.'
            : run.status === 'QUEUED' || run.status === 'RUNNING'
              ? 'This scan is still running — the grade appears when it finishes.'
              : 'The scan did not complete, so there is no security grade.'}
        </p>
        {error && <pre className="mt-2 overflow-x-auto text-xs whitespace-pre-wrap text-danger-text">{error}</pre>}
      </div>
      <SecurityChecks steps={result?.steps ?? []} />
    </>
  );
}
